'use client';

import { SlotRenderer } from '@/components/SlotRenderer';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';
import type { BlockDto } from '@/types/blocks';

/**
 * The article's body, at the position an editor gave this block on the article
 * Template Page (spec 10.03.02.02.07). The intro, the contents list and the
 * author box are sibling blocks; the title, read-time line and author chip are
 * the route's header.
 *
 * No `BlockMapProvider` of its own: it renders inside `LiveRenderTree`'s stack,
 * so the nested `SlotRenderer` inherits `useBlockMap`, the resolved map and the
 * render context — the pattern `layouts.gradient-section` uses for its zone.
 *
 * No wrapper element either: the `<article>` landmark comes from the render
 * tree and encloses the whole block zone.
 */
export const MagazineArticleContentComponent = () => {
  const ctx = useOptionalRenderContext();

  // Inert on any other Page and empty in a Template Page preview: no other
  // route puts an article in the render context (§ AC 1, § AC 2).
  if (!ctx?.entities.article) {
    return null;
  }

  // Each top-level Collapsible Block already carries the `anchorId` the backend
  // derived alongside the `Inhalt` entries (§ AC 3), so nothing is derived here.
  const blocks = (ctx.entities.article.blocks ?? []) as BlockDto[];

  /*
   * A pass-through: the article body *is* the page's content, so this zone owes
   * its blocks the page container exactly as `PageRenderer` does. The widget
   * itself takes none (`layout: 'self'`), or every block would be wrapped twice
   * (70.14-Widget-Width).
   */
  return <SlotRenderer blocks={blocks} zoneName="blocks" container="page" />;
};
